import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import api from '../services/api';

const CATEGORY_OPTIONS = [
  ['attendance_evidence', 'Attendance evidence'],
  ['digital_runtime_only', 'Digital runtime only'],
  ['no_evidence', 'No evidence'],
  ['completed_pending_report', 'Pending lesson report'],
  ['completed_verified', 'Completed and verified'],
  ['needs_review', 'Needs review'],
  ['disputed', 'Disputed'],
  ['student_missed', 'Student missed'],
  ['tutor_no_show', 'Tutor no-show'],
  ['cancelled', 'Cancelled'],
  ['not_held', 'Not held'],
];

const FINAL_OUTCOMES = [
  ['needs_review', 'Needs review'], ['completed_verified', 'Completed and verified'],
  ['student_missed', 'Student missed'], ['tutor_no_show', 'Tutor no-show'],
  ['cancelled', 'Cancelled'], ['rescheduled', 'Rescheduled'], ['not_held', 'Not held'], ['disputed', 'Disputed'],
];

const humanize = (value) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
const formatDate = (value) => value ? new Date(value).toLocaleString() : '-';

const InPersonReconciliation = () => {
  const [filters, setFilters] = useState({ category: 'attendance_evidence', program: '' });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ outcome: 'needs_review', durationMinutes: 60, actualStartAt: '', notes: '', learnerAttendance: [] });

  const params = useMemo(() => ({ ...filters, limit: 200 }), [filters]);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/in-person-reconciliation/admin', { params });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || {});
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load in-person reconciliation.');
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const openItem = (item) => {
    const evidenceByStudent = new Map((item.evidence?.attendance || []).map((row) => [String(row.studentId), row.status]));
    const scheduledMinutes = Math.round((new Date(item.scheduledEndAt) - new Date(item.scheduledStartAt)) / 60000);
    setForm({
      outcome: item.reconciliation?.outcome || 'needs_review',
      durationMinutes: item.reconciliation?.confirmedDurationMinutes || Math.min(480, Math.max(1, scheduledMinutes || 60)),
      actualStartAt: item.actualStartAt ? new Date(item.actualStartAt).toISOString().slice(0, 16) : new Date(item.scheduledStartAt).toISOString().slice(0, 16),
      notes: item.reconciliation?.notes || '',
      learnerAttendance: (item.learners || []).map((learner) => ({
        studentId: learner._id, name: learner.name, status: evidenceByStudent.get(String(learner._id)) || 'not_recorded',
      })),
    });
    setSelected(item);
  };

  const saveDecision = async () => {
    setLoading(true); setError('');
    try {
      await api.patch(`/in-person-reconciliation/admin/${selected.sessionId}`, form);
      setSelected(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save reconciliation decision.');
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <Stack spacing={2}>
        <Card><CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={800}>In-person evidence queue</Typography>
              <Typography variant="body2" color="text.secondary">Only completed and verified records contribute to analytics. Decisions do not change packages, invoices, or remuneration.</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(summary).map(([key, count]) => <Chip key={key} label={`${humanize(key)}: ${count}`} variant="outlined" />)}
            </Box>
            <AdminActionGroup>
              <FormControl size="small" sx={{ minWidth: 220 }}><InputLabel>Evidence/status</InputLabel><Select label="Evidence/status" value={filters.category} onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value }))}>
                <MenuItem value="">All records</MenuItem>{CATEGORY_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select></FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Program</InputLabel><Select label="Program" value={filters.program} onChange={(event) => setFilters((current) => ({ ...current, program: event.target.value }))}>
                <MenuItem value="">All programs</MenuItem><MenuItem value="general">General</MenuItem><MenuItem value="matric">Matric</MenuItem><MenuItem value="math">Math</MenuItem>
              </Select></FormControl>
              <Button variant="contained" onClick={load} disabled={loading}>Refresh</Button>
            </AdminActionGroup>
          </Stack>
        </CardContent></Card>
        {error && <Alert severity="error">{error}</Alert>}
        <Card><TableContainer><Table size="small">
          <TableHead><TableRow><TableCell>Lesson</TableCell><TableCell>Tutor</TableCell><TableCell>Learners</TableCell><TableCell>Evidence</TableCell><TableCell>Timing</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
          <TableBody>
            {loading && !items.length ? <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={28} /></TableCell></TableRow> : null}
            {!loading && !items.length ? <TableRow><TableCell colSpan={6}><Alert severity="info">No lessons match these filters.</Alert></TableCell></TableRow> : null}
            {items.map((item) => <TableRow key={item.sessionId} hover>
              <TableCell><strong>{item.subject}</strong><br /><Typography variant="caption">{formatDate(item.scheduledStartAt)} · {humanize(item.program)}</Typography></TableCell>
              <TableCell>{item.tutor?.name || '-'}</TableCell><TableCell>{item.learners?.length || 0}</TableCell>
              <TableCell><Chip size="small" label={humanize(item.evidence?.category)} color={item.evidence?.category === 'completed_verified' ? 'success' : 'warning'} /></TableCell>
              <TableCell>{item.actualDurationMinutes ? `${item.actualDurationMinutes} actual min` : 'No verified duration'}</TableCell>
              <TableCell align="right"><Button variant="outlined" onClick={() => openItem(item)}>Review</Button></TableCell>
            </TableRow>)}
          </TableBody>
        </Table></TableContainer></Card>
      </Stack>
      <Dialog open={Boolean(selected)} onClose={() => !loading && setSelected(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Reconcile in-person lesson</DialogTitle>
        <DialogContent dividers><Stack spacing={2}>
          <Alert severity="warning">Do not verify a lesson from its schedule alone. Confirm attendance and actual duration from supporting evidence.</Alert>
          <Typography><strong>{selected?.subject}</strong> · {formatDate(selected?.scheduledStartAt)} · {selected?.tutor?.name}</Typography>
          <FormControl fullWidth><InputLabel>Outcome</InputLabel><Select label="Outcome" value={form.outcome} onChange={(event) => setForm((current) => ({ ...current, outcome: event.target.value }))}>{FINAL_OUTCOMES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
          {form.outcome === 'completed_verified' && <>
            <TextField label="Actual start" type="datetime-local" value={form.actualStartAt} onChange={(event) => setForm((current) => ({ ...current, actualStartAt: event.target.value }))} InputLabelProps={{ shrink: true }} required />
            <TextField label="Verified duration in minutes" type="number" value={form.durationMinutes} onChange={(event) => setForm((current) => ({ ...current, durationMinutes: event.target.value }))} inputProps={{ min: 1, max: 480, step: 1 }} required />
            <Typography variant="subtitle2" fontWeight={800}>Learner attendance</Typography>
            {form.learnerAttendance.map((row, index) => <Stack key={row.studentId} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}><Typography sx={{ flex: 1 }}>{row.name}</Typography><FormControl size="small" sx={{ minWidth: 180 }}><Select value={row.status} onChange={(event) => setForm((current) => ({ ...current, learnerAttendance: current.learnerAttendance.map((entry, rowIndex) => rowIndex === index ? { ...entry, status: event.target.value } : entry) }))}><MenuItem value="not_recorded">Not recorded</MenuItem><MenuItem value="present">Present</MenuItem><MenuItem value="late">Late</MenuItem><MenuItem value="absent">Absent</MenuItem><MenuItem value="excused">Excused</MenuItem></Select></FormControl></Stack>)}
          </>}
          <TextField label="Reconciliation notes" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} multiline minRows={3} required helperText="Required for the audit trail; minimum 10 characters." />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)} disabled={loading}>Cancel</Button><Button variant="contained" onClick={saveDecision} disabled={loading || form.notes.trim().length < 10}>Save audited decision</Button></DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default InPersonReconciliation;
